import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/context/AuthContext";
import { getPosition } from "@/lib/geo";
import { useCrumbs } from "@/context/CrumbContext";
import FieldFindingSheet from "@/components/FieldFindingSheet";
import { ArrowLeft, Camera, ChevronRight, Crosshair, Loader2, Paperclip } from "lucide-react";

const OPEN = new Set(["Eksik veri", "Uygunsuz", "Sahada doğrulanmalı"]);
const statusCls = (s) => s === "Uygun" ? "ok" : s === "Uygunsuz" ? "bad" : "warn";

export default function FieldMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");
  const [selected, setSelected] = useState(null);
  const [gps, setGps] = useState({ state: "pending" });
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await apiClient.get(`/assessments/${id}`); setData(data); }
    catch (err) { setToast(err?.response?.data?.detail || "Değerlendirme yüklenemedi"); }
  }, [id]);

  const locate = async () => {
    setGps({ state: "pending" });
    const geo = await getPosition();
    setGps(geo ? { state: "ok", ...geo } : { state: "off" });
  };

  useEffect(() => { load(); locate(); }, [load]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 2600); return () => clearTimeout(t); }, [toast]);

  const findings = useMemo(() => {
    const rows = data?.findings || [];
    return filter === "open" ? rows.filter((f) => OPEN.has(f.status)) : rows;
  }, [data, filter]);

  useCrumbs([{ label: "Değerlendirmeler", to: "/assessments" }, { label: data?.assessment?.title || "…", to: `/assessments/${id}` }, { label: "Saha modu" }]);
  if (!data) return <div className="auth-splash" data-testid="field-loading"><Loader2 className="spin" size={22}/><span>Saha modu hazırlanıyor…</span></div>;
  const openCount = data.findings.filter((f) => OPEN.has(f.status)).length;

  return (
    <div className="field-shell" data-testid="field-mode">
      <header className="field-top">
        <button className="icon-button" onClick={() => navigate(`/assessments/${id}`)} data-testid="field-back"><ArrowLeft size={18}/></button>
        <div className="field-title">
          <div className="eyebrow">SAHA MODU · {data.building?.name}</div>
          <h1 data-testid="field-title">{data.assessment.title}</h1>
        </div>
      </header>
      <button className={`field-gps ${gps.state}`} onClick={locate} data-testid="field-gps-status">
        <Crosshair size={14}/>
        {gps.state === "ok" && <>GPS hazır · {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{Math.round(gps.accuracy)} m</>}
        {gps.state === "pending" && <>Konum alınıyor…</>}
        {gps.state === "off" && <>GPS kapalı · konum izni verin (dokunarak tekrar deneyin)</>}
      </button>
      <div className="field-filter" data-testid="field-filter">
        <button className={filter === "open" ? "active" : ""} onClick={() => setFilter("open")} data-testid="field-filter-open">Açık · {openCount}</button>
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")} data-testid="field-filter-all">Tümü · {data.findings.length}</button>
      </div>
      <div className="field-list">
        {findings.map((f, i) => (
          <button key={f.id} className="field-card" style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }} onClick={() => setSelected(f)} data-testid={`field-finding-${f.id}`}>
            <span className={`field-dot ${statusCls(f.status)}`}/>
            <div className="field-card-main">
              <strong>{f.title}</strong>
              <small>{f.status}{f.quantity ? ` · ${f.quantity} ${f.unit || ""}` : ""}{f.has_evidence ? " · " : ""}{f.has_evidence && <Paperclip size={11}/>}</small>
            </div>
            <Camera size={18} className="field-card-cam"/>
            <ChevronRight size={16}/>
          </button>
        ))}
        {!findings.length && <div className="field-empty">Açık bulgu kalmadı. Tüm maddeler sahada doğrulandı.</div>}
      </div>
      {selected && <FieldFindingSheet finding={selected} assessmentId={id} onClose={() => setSelected(null)} onChanged={load} onToast={setToast}/>}
      {toast && <div className="toast field-toast" data-testid="field-toast">{toast}</div>}
    </div>
  );
}
